import { inventoryService } from "../../services";
import {
  toastType,
  EMPTY_STRING,
  globalConstants,
  inventoryConstants,
  BATCH_INVENTORY_DEFAULT_PRODUCT_OBJECT,
  TCG_PLAYER_API_SUBCATEGORY,
} from "../../constants";
import {
  PRODUCT_TYPES_ENUMS,
  PRODUCT_CONDITIONS_ENUMS,
  TCG_PLAYER_API_GRADE_ENUMS,
  TRADING_CARD_CATEGORIES_ENUMS,
} from "../../system/globalEnums";
import {
  convertToUsd,
  customToast,
  getShortestSku,
  getProductSubCategory,
  handleCreateSelectOptions,
  isStoreCustomProduct,
  isTradingCard,
  isVideoGame,
  handleRaritiesChangeInSingleAddEdit,
} from "../utility";

// This function is used to beautify the condition strings from TCGPlayer API
const beautifyTcgPlayerApiCondition = (conditionStrings) => {
  const mapCondition = (c) => {
    let res = {};
    let tempIndex = -1;

    tempIndex = inventoryConstants.TRADING_CARD_CONDITION.findIndex(
      (conditionConstant) => {
        return conditionConstant.label.includes(c);
      }
    );

    // "Near Mint" to "Near Mint (NM)", "Damaged" to "Damaged (D)" etc
    if (tempIndex !== -1) {
      res = inventoryConstants.TRADING_CARD_CONDITION[tempIndex].label;
    }
    // Return as it is
    else {
      res = c;
    }
    return res;
  };
  return Array.isArray(conditionStrings)
    ? conditionStrings.map((condition) => {
        return mapCondition(condition);
      })
    : mapCondition(conditionStrings);
};

const getTradingCardConditionListForSelectedGrade = (
  selectedGrade,
  subcategory = EMPTY_STRING
) => {
  let conditionList = [];
  const gradeValue = selectedGrade.value ? selectedGrade.value : selectedGrade;
  switch (gradeValue) {
    case TCG_PLAYER_API_GRADE_ENUMS.UNGRADED:
      if (subcategory === TCG_PLAYER_API_SUBCATEGORY.SEALED_PRODUCT) {
        conditionList = handleCreateSelectOptions(
          inventoryConstants.TCG_PLAYER_API_SUBCATEGORY_SEALED_PRODUCT_CONDITIONS
        );
      } else {
        conditionList = inventoryConstants.TRADING_CARD_CONDITION;
      }
      break;
    case TCG_PLAYER_API_GRADE_ENUMS.BECKETT_GRADED:
    case TCG_PLAYER_API_GRADE_ENUMS.CGC_GRADED:
      conditionList = handleCreateSelectOptions(
        inventoryConstants.TCG_PLAYER_API_GRADE_CONDITION
      );
      break;
    default:
      conditionList = handleCreateSelectOptions(
        inventoryConstants.TCG_PLAYER_API_GRADE_CONDITION.slice(0, -2)
      );
      break;
  }

  return conditionList;
};

const getTradingCardLanguageListForSelectedCondition = (
  tcgPlayerApiProductObj,
  selectedCondition
) => {
  let selectedConditionValue = selectedCondition?.value
    ? selectedCondition.value
    : selectedCondition;
  let tempLanguageListSet = new Set();
  let isIncludeLanguage = false;
  let languageList = [];
  if (tcgPlayerApiProductObj.skus) {
    tcgPlayerApiProductObj.skus.forEach((sku) => {
      isIncludeLanguage = sku.condition.some((cond, index) => {
        return (
          beautifyTcgPlayerApiCondition(cond.name) === selectedConditionValue
        );
      });
      isIncludeLanguage && tempLanguageListSet.add(sku.language.name);
    });
  }
  languageList = Array.from(tempLanguageListSet.values());
  return handleCreateSelectOptions(languageList);
};

const getTradingCardRarityListForSelectedLanguage = (
  tcgPlayerApiProductObj,
  selectedLanguage
) => {
  let tempRarityListSet = new Set();
  let isIncludeRarity = false;
  let cardRarityList = [];
  if (tcgPlayerApiProductObj.skus) {
    tcgPlayerApiProductObj.skus.forEach((sku) => {
      isIncludeRarity = sku.language.name === selectedLanguage;
      isIncludeRarity && tempRarityListSet.add(sku.printing.name);
    });
  }
  cardRarityList = Array.from(tempRarityListSet.values());
  return handleCreateSelectOptions(cardRarityList);
};

const getTradingCardMarketPriceForSelectedProperties = (
  tcgPlayerApiProductObj,
  selectedLanguage,
  selectedCondition,
  selectedCardRarity,
  isConditionMapObject = false
) => {
  let marketPrice = 0;
  let tempNearMintMarketPriceForSelectedProperties = 0;
  let selectedConditionValue =
    selectedCondition?.value ||
    selectedCondition ||
    tcgPlayerApiProductObj.condition?.value ||
    tcgPlayerApiProductObj.condition ||
    tcgPlayerApiProductObj.productCondition?.value ||
    tcgPlayerApiProductObj.productCondition ||
    EMPTY_STRING;
  let selectedLanguageValue =
    selectedLanguage?.value ||
    selectedLanguage ||
    tcgPlayerApiProductObj.language;
  let selectedCardRarityValue =
    selectedCardRarity?.value ||
    selectedCardRarity ||
    tcgPlayerApiProductObj.cardRarity;

  if (tcgPlayerApiProductObj.skus) {
    tcgPlayerApiProductObj.skus.forEach((sku) => {
      if (
        sku.language.name === selectedLanguageValue &&
        sku.printing.name === selectedCardRarityValue &&
        marketPrice === 0
      ) {
        sku.condition.forEach((cond, index) => {
          if (
            beautifyTcgPlayerApiCondition(cond.name) === selectedConditionValue
          ) {
            marketPrice = sku.prices[index].marketPrice;
          }
          if (
            beautifyTcgPlayerApiCondition(cond.name) ===
            PRODUCT_CONDITIONS_ENUMS.NEAR_MINT
          ) {
            tempNearMintMarketPriceForSelectedProperties =
              sku.prices[index].marketPrice;
          }
        });
      }
    });
  }

  // If marketPrice = 0 for selected properties then return the first condition's marketPrice (near-mint) of selected language, printing combination
  if (marketPrice === 0) {
    marketPrice = tempNearMintMarketPriceForSelectedProperties;
  }

  if (isConditionMapObject) {
    marketPrice = {
      [selectedConditionValue]: {
        marketPrice: marketPrice,
        suggestedSellPrice: marketPrice,
      },
    };
  }

  return marketPrice;
};

// Returns the mapped object that will be used everywhere (1. Third Party API Search Options, 2. Cart Items)
const handleMapTcgObjectToDbJson = (tcgProduct, initialValuesObject = {}) => {
  // Initializations
  let mappedObject = {};
  // Helper Functions
  function extractCardDetails(obj) {
    // Initialize the variables
    let rarity = EMPTY_STRING;
    let cardRarity = EMPTY_STRING;
    let condition = {};
    let set = EMPTY_STRING;
    let grade = EMPTY_STRING;
    let language = EMPTY_STRING;
    let cardRarityList = [];
    let gradeList = [];
    let languageList = [];
    let conditionList = [];
    let marketPrice = 0;
    let subcategory = EMPTY_STRING;
    let maxConditionId = -1;
    let tempGradeValue =
      initialValuesObject?.price?.grade || TCG_PLAYER_API_GRADE_ENUMS.UNGRADED; // as default selected grading is Ungraded
    // Extract the set name from the object
    set = obj?.setORGroupName?.name ?? EMPTY_STRING;

    // Extract the rarity from the object
    rarity = initialValuesObject?.price?.rarity || obj.Rarity || EMPTY_STRING;

    // Populate the raritiesList by extracting the "Rarity" value from extendedData
    if (obj.extendedData) {
      obj.extendedData.forEach((data) => {
        if (data.name === "Rarity" && !cardRarityList.includes(data.value)) {
          cardRarityList.push(data.value);
        }
      });
    }

    // Extract subcategory, conditionsList and languageList
    if (obj.skus) {
      obj.skus.forEach((sku) => {
        sku.condition.forEach((cond, index) => {
          if (!conditionList.includes(cond.name)) {
            conditionList.push(cond.name);
          }
          maxConditionId =
            Number(cond.conditionId) > maxConditionId
              ? Number(cond.conditionId)
              : maxConditionId;
        });

        // Populate the languageList by extracting language from each SKU
        if (sku.language && !languageList.includes(sku.language.name)) {
          languageList.push(sku.language.name);
        }
      });
      // Assign subcategory
      subcategory =
        maxConditionId > 6
          ? TCG_PLAYER_API_SUBCATEGORY.OTHER
          : maxConditionId === 6
          ? TCG_PLAYER_API_SUBCATEGORY.SEALED_PRODUCT
          : TCG_PLAYER_API_SUBCATEGORY.SINGLES;
    }

    gradeList = handleCreateSelectOptions(
      Object.values(TCG_PLAYER_API_GRADE_ENUMS)
    );
    grade = tempGradeValue;
    conditionList = getTradingCardConditionListForSelectedGrade(
      tempGradeValue,
      subcategory
    );
    condition = handleCreateSelectOptions(
      initialValuesObject?.price?.type ?? conditionList?.[0] ?? EMPTY_STRING
    );
    languageList = handleCreateSelectOptions(languageList);
    language =
      initialValuesObject?.price?.language ||
      initialValuesObject?.language ||
      languageList[0]?.value ||
      EMPTY_STRING;
    cardRarityList = getTradingCardRarityListForSelectedLanguage(
      tcgProduct,
      language
    );
    cardRarity =
      initialValuesObject?.cardRarity ??
      cardRarityList?.[0]?.value ??
      cardRarity;
    set = initialValuesObject?.price?.set || set || EMPTY_STRING;
    subcategory =
      subcategory || initialValuesObject?.subcategory || EMPTY_STRING;
    marketPrice =
      getTradingCardMarketPriceForSelectedProperties(
        tcgProduct,
        language,
        condition.label,
        cardRarity
      ) || 0;

    // Return the result
    return {
      set,
      grade,
      cardRarity,
      language,
      condition,
      productCondition: condition,
      gradeList,
      cardRarityList,
      languageList,
      conditionList,
      productConditionList: conditionList,
      marketPrice,
      subcategory,
      genre: subcategory,
      rarity,
      price: marketPrice,
    };
  }
  const id = tcgProduct?.id || initialValuesObject?.id || EMPTY_STRING;
  const productName =
    tcgProduct?.title || initialValuesObject?.product_name || EMPTY_STRING;
  const category =
    tcgProduct.category?.name ||
    initialValuesObject?.category_name ||
    EMPTY_STRING;
  const apiSource = inventoryConstants.TCG_PLAYER_API;
  const inStockPrice = initialValuesObject?.inStockPrice ?? 0;
  mappedObject = {
    ...BATCH_INVENTORY_DEFAULT_PRODUCT_OBJECT,
    ...tcgProduct,
    id: id,
    productObject: Object.keys(tcgProduct).length
      ? tcgProduct
      : { ...initialValuesObject },
    // properties for select menu options
    price: 0,
    name: productName,
    sourceApi: apiSource,
    apiSource: apiSource,
    productType: PRODUCT_TYPES_ENUMS.TRADING_CARD,
    // properties for selected product object (once product is selected from options)
    productId: id,
    category: category,
    consoleName: category,
    productName: productName,
    "product-name": productName,
    tcgPlayerUrl: tcgProduct.tcgUrl,
    imgUrl: tcgProduct?.imageUrl || initialValuesObject?.imgUrl || EMPTY_STRING,
    upc: initialValuesObject?.upc ?? EMPTY_STRING,
    epid: initialValuesObject?.epid ?? EMPTY_STRING,
    skuNumber: initialValuesObject?.skuNumber ?? [],
    cardNumber: initialValuesObject?.cardNumber ?? 0,
    maxBuyPrice: initialValuesObject?.maxBuyPrice ?? 0,
    costOfGoods: initialValuesObject?.costOfGoods ?? 0,
    inStockPrice: inStockPrice,
    onlineUnitSellPrice:
      initialValuesObject?.onlineUnitSellPrice ?? inStockPrice,
    inStorePrice: initialValuesObject?.inStorePrice ?? 0,
    barcode: initialValuesObject?.barcode ?? EMPTY_STRING,
    averageBuyPrice: initialValuesObject?.averageBuyPrice ?? 0,
    averageSellPrice: initialValuesObject?.averageSellPrice ?? 0,
    gameStopBuyPrice: initialValuesObject?.gameStopBuyPrice ?? 0,
    suggestedSellPrice: initialValuesObject?.suggestedSellPrice ?? 0,
    [inventoryConstants.PRICECHARTING_PRODUCT_CONSOLE_NAME]: category,
    inStoreQuantity: 0, // it is used as inStockQuantity in batch as in batch inStockQuantity represent quantity
    inStockQuantity:
      initialValuesObject?.inStockQuantity || (tcgProduct.isBatch ? 1 : 0), //Use initalValue if not provided then in batch default value is 1 as inStockQuantity represents quantity in all batch modals otherwise default will be 0 as inStockQuantity is calculated based on store stock
    ...extractCardDetails(tcgProduct),
  };
  mappedObject = handleCreateThirdPartyApiProductInfoObj(mappedObject, true);

  return mappedObject;
};

const handleCreateThirdPartyApiProductInfoObj = (option, isTradeOptions) => {
  let tempValue = {
    ...option,
    product_id: option.id,
    sourceApi: option.sourceApi,
    genre: option.genre,
    product_name: option.name,
    category_name: option.category,
    productType: option.productType,
    imgUrl: option?.imgUrl ?? EMPTY_STRING,
    upc: option.upc,
    productObject: option.productObject,
  };
  if (isTradeOptions) {
    tempValue = {
      ...tempValue,
      id: option.id,
      name: option.name,
      price: option.price,
    };
  }
  return tempValue;
};

export const handleGetFilterLoadOptions = (
  options,
  searchProductType,
  productCategory,
  productSubCategory
) => {
  const selectedType = searchProductType?.label;
  const selectedCategory = productCategory?.label;
  const selectedSubCategory = productSubCategory?.label;
  let tempIsIncludeProduct = false;

  let filteredOptions = options.filter((product) => {
    tempIsIncludeProduct =
      (selectedType === PRODUCT_TYPES_ENUMS.ALL ||
        product.productType === selectedType) &&
      (!selectedCategory || product.category === selectedCategory) &&
      (!selectedSubCategory || product.subcategory === selectedSubCategory);

    return tempIsIncludeProduct;
  });

  return filteredOptions;
};

//-------craete combine list for the api result list
const createCombinedList = (apiData, sourceApi) => {
  return apiData.map((item) => {
    if (sourceApi === inventoryConstants.PRICECHARTING_API) {
      return {
        id: item.id,
        price: convertToUsd(item["cib-price"]) ?? 0,
        name: item[inventoryConstants.PRICECHARTING_PRODUCT_NAME],
        category: item[inventoryConstants.PRICECHARTING_PRODUCT_CONSOLE_NAME],
        subcategory: getProductSubCategory(item.genre),
        sourceApi: sourceApi,
        productType: item.productType,
        genre: item.genre,
        upc: item.upc || EMPTY_STRING,
        productObject: item,
      };
    } else if (sourceApi === inventoryConstants.TCG_PLAYER_API) {
      return {
        ...handleMapTcgObjectToDbJson(item),
      };
    } else if (sourceApi === inventoryConstants.STORE_CUSTOM_PRODUCT) {
      return {
        id: getShortestSku(item.sku),
        name: `${item.product_name}`,
        category: item.category_name,
        sourceApi: sourceApi,
        price: item?.price?.unit_sell_price ?? 0,
        genre: item.category_name,
        subcategory: item.subcategory,
        upc: item.upc,
        productObject: item,
        productType: item.productType,
      };
    }
  });
};

const handleFetchPriceChartingApiProducts = async (
  searchValue,
  storeCategories = []
) => {
  try {
    let priceChartingRes = await inventoryService.getPricechartingApiProducts(
      searchValue
    );
    let priceChartingProducts = priceChartingRes?.products;
    // Excluding additional pricecharting products i.e Yogiho, Magic Card etc and only return the products with category that matches the store system categories
    const storeSystemCategoriesArr = storeCategories
      .filter((item) => !item.isCustom)
      ?.map((obj) => obj.name);
    let filteredPriceChartingProducts = priceChartingProducts.filter(
      (obj) =>
        storeSystemCategoriesArr.includes(obj["console-name"]) &&
        obj.genre !== TRADING_CARD_CATEGORIES_ENUMS.POKEMON_CARD &&
        obj.genre !== TRADING_CARD_CATEGORIES_ENUMS.MAGIC_CARD
    );
    return filteredPriceChartingProducts;
  } catch (error) {
    console.log(error);
    customToast(error, toastType.ERROR);
    return [];
  }
};

const handleFetchTcgPlayerApiProducts = async (queryData) => {
  try {
    const response = await inventoryService.getTcgPlayerApiProducts(queryData);
    return response;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const getExternalApiProductsCombineList = async (props) => {
  const {
    isBatch = false,
    searchValue,
    currentStore,
    customCategories,
    isSearchCustomProduct = false,
    searchProductType: searchProductTypeObj,
    productCategory: searchProductCategoryObj,
    productSubCategory: searchProductSubCategoryObj,
  } = props;
  let tcgPlayerOptions = [];
  let priceChartingOptions = [];
  let customProductOptions = [];

  // Pricecharting API
  if (
    searchProductTypeObj?.label === PRODUCT_TYPES_ENUMS.ALL ||
    searchProductTypeObj?.label === PRODUCT_TYPES_ENUMS.VIDEO_GAME
  ) {
    priceChartingOptions = await handleFetchPriceChartingApiProducts(
      searchValue,
      customCategories
    );
    priceChartingOptions = addAdditionalPropertiesInApiProductsObj(
      priceChartingOptions,
      { isBatch }
    );
  }
  // TcgPlayer API
  // if (
  //   searchProductTypeObj?.label === PRODUCT_TYPES_ENUMS.ALL ||
  //   searchProductTypeObj?.label === PRODUCT_TYPES_ENUMS.TRADING_CARD
  // ) {
  //   tcgPlayerOptions = await handleFetchTcgPlayerApiProducts({
  //     searchValue: searchValue,
  //     category: searchProductCategoryObj?.label ?? EMPTY_STRING,
  //   });
  //   tcgPlayerOptions = addAdditionalPropertiesInApiProductsObj(
  //     tcgPlayerOptions,
  //     { isBatch }
  //   );
  // }
  // Store Custom Products
  if (isSearchCustomProduct) {
    customProductOptions = await inventoryService
      .getStoreCustomProductsByName(currentStore?.id, {
        productName: searchValue,
      })
      .then(
        (response) => {
          return response;
        },
        (error) => {
          //customToast(error, toastType.ERROR);
          return [];
        }
      );
  }

  const apiProductsUnionArr = [
    ...createCombinedList(
      priceChartingOptions,
      inventoryConstants.PRICECHARTING_API
    ),
    ...createCombinedList(
      tcgPlayerOptions || [],
      inventoryConstants.TCG_PLAYER_API
    ),
    ...createCombinedList(
      customProductOptions || [],
      inventoryConstants.STORE_CUSTOM_PRODUCT
    ),
  ];

  // Apply user selected type, category, subcategory filter
  let filteredCombinedApiProducts = handleGetFilterLoadOptions(
    apiProductsUnionArr,
    searchProductTypeObj,
    searchProductCategoryObj,
    searchProductSubCategoryObj
  );

  return filteredCombinedApiProducts;
};

const createSearchInventoryApiOptionsList = (
  filteredOptions,
  isTradeOptions = false
) => {
  const selectOptions = filteredOptions?.map((option) => {
    return {
      value: handleCreateThirdPartyApiProductInfoObj(option, isTradeOptions),
      label: (
        <div className="search-option-wrapper">
          <span className="console-name-react-select">{option.category}</span>
          <span>{option.name}</span>
        </div>
      ),
    };
  });
  return selectOptions;
};

// add additional properties i.e productType, isBatch etc in api object that is not included by the api
const addAdditionalPropertiesInApiProductsObj = (
  products,
  additionalPropertiesToAdd = {
    isBatch: false,
  }
) => {
  const newProductObject = products.map((item) => {
    if (item.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD) {
      return {
        ...item,
        ...additionalPropertiesToAdd,
        productType: PRODUCT_TYPES_ENUMS.TRADING_CARD,
      };
    } else {
      return {
        ...item,
        ...additionalPropertiesToAdd,
        productType: PRODUCT_TYPES_ENUMS.VIDEO_GAME,
      };
    }
  });
  return newProductObject;
};

const handleOnChangeTradingCardRarity = (
  selectedCardRarity,
  priceChartingProductInfo,
  tradeinMargin = null,
  markupPresets = null
) => {
  // Update marketPrice and cardRarity
  let updatedPriceChartingProductInfo = {
    ...priceChartingProductInfo,
    productMetaData: {
      ...priceChartingProductInfo.productMetaData,
      cardRarity: selectedCardRarity,
      marketPrice: getTradingCardMarketPriceForSelectedProperties(
        priceChartingProductInfo.productMetaData,
        undefined,
        undefined,
        selectedCardRarity
      ),

      suggestedSellPrice: 0,
    },
  };
  // Update in stockPrice, cashOffer, tradeOffer etc
  if (tradeinMargin && markupPresets) {
    updatedPriceChartingProductInfo.productMetaData =
      handleRaritiesChangeInSingleAddEdit(
        selectedCardRarity,
        () => {},
        () => {},
        updatedPriceChartingProductInfo.productMetaData,
        tradeinMargin,
        markupPresets
      );
  }

  return updatedPriceChartingProductInfo;
};

const handleOnChangeTradingCardLanguage = (
  selectedLanguage,
  priceChartingProductInfo
) => {
  let updatedPriceChartingProductInfo = {
    ...priceChartingProductInfo,
    productMetaData: {
      ...priceChartingProductInfo.productMetaData,
      language: selectedLanguage,
      cardRarityList: getTradingCardRarityListForSelectedLanguage(
        priceChartingProductInfo.productMetaData,
        selectedLanguage
      ),
    },
  };

  return handleOnChangeTradingCardRarity(
    updatedPriceChartingProductInfo.productMetaData.cardRarityList[0]?.value ||
      EMPTY_STRING,
    updatedPriceChartingProductInfo
  );
};

const handleOnchangeTradingCardCondition = (
  selectedCondition,
  priceChartingProductInfo
) => {
  let updatedPriceChartingProductInfo = {
    ...priceChartingProductInfo,
    productMetaData: {
      ...priceChartingProductInfo.productMetaData,
      condition: selectedCondition,
      productCondition: selectedCondition,
      marketPrice: getTradingCardMarketPriceForSelectedProperties(
        priceChartingProductInfo.productMetaData,
        undefined,
        selectedCondition,
        undefined
      ),
    },
  };

  return updatedPriceChartingProductInfo;
};

export {
  createCombinedList,
  handleMapTcgObjectToDbJson,
  beautifyTcgPlayerApiCondition,
  handleFetchTcgPlayerApiProducts,
  handleOnChangeTradingCardRarity,
  getExternalApiProductsCombineList,
  handleOnChangeTradingCardLanguage,
  handleOnchangeTradingCardCondition,
  handleFetchPriceChartingApiProducts,
  createSearchInventoryApiOptionsList,
  addAdditionalPropertiesInApiProductsObj,
  getTradingCardConditionListForSelectedGrade,
  getTradingCardMarketPriceForSelectedProperties,
};
